/* eslint-disable require-jsdoc */
import { fireDelayedHistoryChange } from 'app/utilities/gtm';
import { getPageDataAction } from 'app/ducks/page';
import { isFrontendRoute } from 'app/utilities/routes';
import { startedRoutingAction } from 'app/ducks/status';
import { fetchFilteredNewsAction, MODE } from 'app/ducks/filtered-news';
import {
    getCategoryFilters,
    getListingLayout,
    getListingLimit,
    getListingPages,
    getListingPath,
    getPageNumber,
    isNewsArchivePath,
    LAYOUT,
    NEWS_ARCHIVE_SLUG
} from 'app/utilities/news-listing';
import { isAbsoluteUrl, normalizePath } from 'app/utilities/url';
import { LOCATION_CHANGE, replace } from 'redux-first-history';
import selectPage, { newsCategoriesSelector, newsSubCategoriesSelector } from 'app/selectors/page';

// Handle custom URL redirects before fetch the page data from API
function resolveRedirect(customUrls, action) {
    const { payload } = action;

    const path = normalizePath(payload.location.pathname);

    const redirectTo = customUrls[path];
    if (redirectTo) {
        // support absolute path redirects
        if (isAbsoluteUrl(redirectTo)) {
            window.location.replace(redirectTo);
        }

        return replace(redirectTo);
    }

    return null;
}

/*
    The listing the route being taken is going to need, worked out from the
    categories the page being left already holds rather than from the page being
    navigated to, which has not been fetched yet.

    That is the whole point of doing it here: the listing is filtered by category
    id, and waiting for the page response to learn that id puts the two requests
    in series, which is a second of nothing happening with a loader in the middle
    of it. Naming the category up front lets them go out together.

    Null whenever the category cannot be named with certainty, which is every
    article URL, so no page is ever charged for a listing it does not render.
*/
function resolveListing(state, location) {
    // The archive lists every category, so like the landing page it needs nothing
    // from the response to be named. It pages through slices rather than
    // accumulating, so one page of it is one request.
    if (isNewsArchivePath(location.pathname)) {
        const layout = getListingLayout(NEWS_ARCHIVE_SLUG);

        return {
            category: null,
            layout: layout,
            limit: getListingLimit(layout),
            pages: getListingPages(layout, getPageNumber(location.search))
        };
    }

    const target = getListingPath(location.pathname);

    // A filter in the URL is resolved against collections that only the page
    // being opened carries, so those are left to the listing section itself
    if (!target || getCategoryFilters(location.search).length) {
        return null;
    }

    // The path says which layout the page will use, and so how its page number
    // is read, without having to wait for the response to name the template
    const layout = target.slug ? LAYOUT.GRID : LAYOUT.CAROUSEL;
    const listing = {
        layout: layout,
        limit: getListingLimit(layout),
        pages: getListingPages(layout, getPageNumber(location.search))
    };

    // The landing page has no category of its own, which the endpoint reads as
    // every category
    if (!target.slug) {
        return { ...listing, category: null };
    }

    const from = state.router.location.pathname;
    const fromTarget = getListingPath(from);
    // A level 2 category is only listed by its own parent's page
    const isParentPage = Boolean(fromTarget) && !fromTarget.parent && fromTarget.slug === target.parent;
    const collection = target.parent ?
        (isParentPage ? newsSubCategoriesSelector(state, from) : null) :
        newsCategoriesSelector(state, from);
    const match = (collection || []).find(({ slug }) => slug === target.slug);

    return match ? { ...listing, category: match.id } : null;
}

function createPreRouteMiddleware() {
    return ({ dispatch, getState }) => (next) => (action) => {
        const { payload, type } = action;

        if (type === LOCATION_CHANGE) {
            const state = getState();
            // Check if we need to do redirects
            const redirectAction = resolveRedirect(state.customUrls, action);
            if (redirectAction) {
                dispatch([redirectAction]);

                return;
            }

            const location = payload && payload.location ? payload.location : { pathname: '' };
            const pathname = location.pathname;

            const page = selectPage(state, pathname);

            if (page || isFrontendRoute(pathname)) {
                next(action);

                // On every route change we fire the GTM event 'delayedHistoryChange'.
                fireDelayedHistoryChange();
            } else {
                const listing = resolveListing(state, location);

                dispatch([
                    startedRoutingAction(),
                    // Ahead of the page request rather than after it, so the two
                    // are in flight at the same time
                    ...listing ? [fetchFilteredNewsAction(listing.category, listing.pages, MODE.REPLACE, listing.limit)] : [],
                    getPageDataAction(location)
                ]);
            }
        } else {
            next(action);
        }
    };
}

const preRouteMiddleware = createPreRouteMiddleware();

export default preRouteMiddleware;
